import React, { useState, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import { Link, NavLink, Redirect, Route, Switch, useHistory, useParams, useRouteMatch } from "react-router-dom";
import { File, Files, Trash, Loader2, Bot } from "lucide-react";
import { ReactComponent as BackArrow } from "../../svg/back.svg";
import { GetRagCorpusFiles, DeleteRagFile, UploadRagFile, DeleteRagCorpus } from "../../api/APIGemini";
import { GetCorpusCodes, PostCodes, DeleteCode } from "../../api/APISurvey";
import { GetBots, updateBotById } from "../../api/APIBot";
import { FILE_CONFIG, formatFileSize } from "../../utils";
import { Modal, Popup } from "../../UIElements";
import UsedByBots from "../../components/UsedByBots/UsedByBots";
import { useSnackbar } from "../../Content";
import { queryClient, useCurrentUserData } from "../../App";
import "./KnowledgeCorpusEditor.scss";

export default function KnowledgeCorpusEditor() {
    const intl = useIntl();
    const { corpusId } = useParams();
    let { url, path } = useRouteMatch();
    const { setSnackbarData } = useSnackbar();
    const { currentUserData } = useCurrentUserData();

    const corpusCodesQuery = GetCorpusCodes();
    const botsQuery = GetBots();

    const corpusCodesMap = corpusCodesQuery.data?.[0] || {};
    const codeEntry = corpusCodesMap[corpusId];
    const resourceName = codeEntry?.resourceName;
    const title = codeEntry?.title || corpusId;

    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const [isSavingTitle, setIsSavingTitle] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState({}); // { [filename]: { status, startTime, size } }
    const [, setTick] = useState(0);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeletingFile, setIsDeletingFile] = useState(false);
    const [showDeleteKBModal, setShowDeleteKBModal] = useState(false);
    const [isDeletingKB, setIsDeletingKB] = useState(false);
    const fileInputRef = useRef(null);
    const history = useHistory();

    useEffect(() => {
        document.title = (title || intl.formatMessage({ id: 'knowledge', defaultMessage: 'Knowledge' })) + " | Annie Admin"
    }, [title, intl])

    const isUploading = Object.values(uploadingFiles).some(f => f.status === 'uploading');

    // Warn before closing/refreshing tab during upload
    useEffect(() => {
        if (!isUploading) return;
        const handler = (e) => { e.preventDefault(); };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isUploading]);

    // Tick every second while files are uploading to update elapsed time
    useEffect(() => {
        const hasActive = Object.values(uploadingFiles).some(
            f => f.status === 'uploading' || f.status === 'success'
        );
        if (!hasActive) return;
        const interval = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(interval);
    }, [uploadingFiles]);

    // Derive display phase from elapsed time: "uploading" → "indexing" after estimated upload finishes
    const getUploadPhase = (fileInfo) => {
        if (fileInfo.status !== 'uploading') return fileInfo.status;
        const elapsed = (Date.now() - fileInfo.startTime) / 1000;
        const uploadSeconds = Math.max(3, fileInfo.size / (2 * 1024 * 1024));
        return elapsed > uploadSeconds ? 'indexing' : 'uploading';
    };

    const relativeCorpusName = `ragCorpora/${corpusId}`;
    const { data: filesData, isLoading: filesLoading, refetch: refetchFiles } = GetRagCorpusFiles(relativeCorpusName, !!resourceName);
    const uploadRagFileMutation = UploadRagFile();
    const deleteRagFileMutation = DeleteRagFile();
    const postCodesMutation = PostCodes();
    const deleteRagCorpusMutation = DeleteRagCorpus();
    const deleteCodeMutation = DeleteCode();

    const ragFiles = filesData?.ragFiles || [];
    const savedFiles = codeEntry?.files || {};

    // Merge RAG API data with saved metadata from codes
    const files = ragFiles.map(f => {
        const ragFileId = f.name.split('/').pop();
        const savedMeta = savedFiles[ragFileId];
        return {
            ...f,
            ragFileId,
            size: savedMeta?.size,
            uploadedBy: savedMeta?.uploadedBy,
        };
    });

    const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0);

    // "Used by" — find bots that reference this KB
    const allBots = botsQuery.data || [];
    const usedByBots = allBots.filter(bot =>
        Object.values(bot?.config?.corpora || {}).includes(corpusId)
    );

    const handleStartEditTitle = () => {
        setTitleDraft(title);
        setEditingTitle(true);
    };

    const handleSaveTitle = async () => {
        if (!titleDraft.trim() || titleDraft.trim() === title) {
            setEditingTitle(false);
            return;
        }
        setIsSavingTitle(true);
        try {
            await postCodesMutation.mutateAsync([{
                codeset: 'corpus',
                code: corpusId,
                value: {
                    ...codeEntry,
                    title: titleDraft.trim()
                }
            }]);
            queryClient.invalidateQueries('corpusCodes');
            setEditingTitle(false);
        } catch (err) {
            console.error('Error saving title:', err);
            setSnackbarData({ show: true, text: intl.formatMessage({ id: 'saveFailed', defaultMessage: 'Failed to save changes. Please try again.' }), state: 'error', autoHide: true });
        } finally {
            setIsSavingTitle(false);
        }
    };

    const handleDeleteKB = async () => {
        setIsDeletingKB(true);
        try {
            // Delete from GCP
            try {
                await deleteRagCorpusMutation.mutateAsync(`ragCorpora/${corpusId}`);
            } catch (err) {
                if (err.response?.status !== 404) throw err;
            }

            // Delete from codes table
            try {
                await deleteCodeMutation.mutateAsync({ codeset: 'corpus', code: corpusId });
            } catch (err) {
                await postCodesMutation.mutateAsync([{ codeset: 'corpus', code: corpusId, value: null }]);
            }

            // Remove KB reference from all linked bot modules
            for (const bot of usedByBots) {
                const updatedCorpora = { ...bot.config.corpora };
                for (const [slug, cid] of Object.entries(updatedCorpora)) {
                    if (cid === corpusId) delete updatedCorpora[slug];
                }
                try {
                    await updateBotById(bot.id, { ...bot, config: { ...bot.config, corpora: updatedCorpora } });
                } catch (err) {
                    console.error(`Failed to unlink KB from bot ${bot.id}:`, err);
                }
            }

            queryClient.invalidateQueries('corpusCodes');
            queryClient.invalidateQueries('ragCorpora');
            queryClient.invalidateQueries('bots');
            setSnackbarData({ show: true, text: intl.formatMessage({ id: 'knowledge.deleteSuccess', defaultMessage: 'Knowledge base deleted.' }), state: 'success', autoHide: true });
            history.push('/bot-competences');
        } catch (err) {
            console.error('Error deleting Knowledge Base:', err);
            setSnackbarData({ show: true, text: intl.formatMessage({ id: 'knowledge.deleteError', defaultMessage: 'Failed to delete knowledge base. Please try again.' }), state: 'error', autoHide: true });
        } finally {
            setIsDeletingKB(false);
            setShowDeleteKBModal(false);
        }
    };

    const validateFileTypeGemini = (filename) => {
        const fileExt = '.' + filename.split('.').pop().toLowerCase();
        return FILE_CONFIG.ALLOWED_EXTENSIONS_GEMINI.includes(fileExt);
    };

    const handleFileUpload = async (event) => {
        const selectedFiles = Array.from(event.target.files);
        if (selectedFiles.length === 0) return;

        const validFiles = [];
        for (const file of selectedFiles) {
            if (!validateFileTypeGemini(file.name)) {
                const allowedTypes = FILE_CONFIG.ALLOWED_EXTENSIONS_GEMINI.map(e => e.replace('.', '').toUpperCase()).join(', ');
                setSnackbarData({ show: true, text: intl.formatMessage({ id: 'file.invalidType', defaultMessage: 'Invalid file type: {filename}. Allowed: {types}' }, { filename: file.name, types: allowedTypes }), state: 'error', autoHide: true });
                continue;
            }
            if (file.size > FILE_CONFIG.MAX_FILE_SIZE_GEMINI) {
                setSnackbarData({ show: true, text: intl.formatMessage({ id: 'file.tooLarge', defaultMessage: '{filename} is too large. Maximum size is {maxSize}.' }, { filename: file.name, maxSize: formatFileSize(FILE_CONFIG.MAX_FILE_SIZE_GEMINI) }), state: 'error', autoHide: true });
                continue;
            }
            if (totalSize + file.size > FILE_CONFIG.MAX_TOTAL_SIZE_GEMINI) {
                setSnackbarData({ show: true, text: intl.formatMessage({ id: 'file.totalSizeExceeded', defaultMessage: 'Total size limit exceeded. Maximum total is {maxSize}.' }, { maxSize: formatFileSize(FILE_CONFIG.MAX_TOTAL_SIZE_GEMINI) }), state: 'error', autoHide: true });
                continue;
            }
            validFiles.push(file);
        }

        if (validFiles.length === 0) {
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        const initialState = {};
        validFiles.forEach(f => {
            initialState[f.name] = { status: 'uploading', startTime: Date.now(), size: f.size };
        });
        setUploadingFiles(initialState);

        let successCount = 0;
        const newFileMeta = { ...savedFiles };

        for (const file of validFiles) {
            try {
                const result = await uploadRagFileMutation.mutateAsync({ ragCorpusId: corpusId, file });
                setUploadingFiles(prev => ({ ...prev, [file.name]: { ...prev[file.name], status: 'success' } }));
                successCount++;

                // Save file metadata — extract ragFileId from response
                const ragFileId = result?.ragFile?.name?.split('/').pop();
                if (ragFileId) {
                    newFileMeta[ragFileId] = {
                        filename: file.name,
                        size: file.size,
                        uploadedAt: new Date().toISOString(),
                        uploadedBy: currentUserData?.email || currentUserData?.uid || null,
                    };
                }
            } catch (err) {
                console.error('Upload error:', err);
                // The upload may have actually completed on GCP even though the request errored
                // Check the GCP file list for a matching new file before declaring failure.
                let recoveredId = null;
                try {
                    const { data } = await refetchFiles();
                    const match = (data?.ragFiles || []).find(f => {
                        const id = f.name?.split('/').pop();
                        return f.displayName === file.name && id && !newFileMeta[id];
                    });
                    recoveredId = match?.name?.split('/').pop() || null;
                } catch (recoverErr) {
                    console.error('Upload recovery check failed:', recoverErr);
                }

                if (recoveredId) {
                    newFileMeta[recoveredId] = {
                        filename: file.name,
                        size: file.size,
                        uploadedAt: new Date().toISOString(),
                        uploadedBy: currentUserData?.email || currentUserData?.uid || null,
                    };
                    setUploadingFiles(prev => ({ ...prev, [file.name]: { ...prev[file.name], status: 'success' } }));
                    successCount++;
                } else {
                    setUploadingFiles(prev => ({ ...prev, [file.name]: { ...prev[file.name], status: 'error' } }));
                }
            }
        }

        if (successCount > 0) {
            // Persist file metadata to codes table
            try {
                await postCodesMutation.mutateAsync([{
                    codeset: 'corpus',
                    code: corpusId,
                    value: { ...codeEntry, files: newFileMeta }
                }]);
                queryClient.invalidateQueries('corpusCodes');
            } catch (err) {
                console.error('Error saving file metadata:', err);
            }

            refetchFiles();
            setSnackbarData({ show: true, text: intl.formatMessage({ id: 'file.uploadAllSuccess', defaultMessage: '{count} file(s) uploaded successfully' }, { count: successCount }), state: 'success', autoHide: true });
        }

        setTimeout(() => setUploadingFiles({}), 3000);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const confirmDeleteFile = async () => {
        if (!deleteTarget) return;
        setIsDeletingFile(true);
        try {
            const ragFileId = deleteTarget.ragFileId || deleteTarget.name.split('/').pop();
            await deleteRagFileMutation.mutateAsync({ corpusName: relativeCorpusName, ragFileName: ragFileId });

            // Remove file metadata from codes
            const updatedFiles = { ...savedFiles };
            delete updatedFiles[ragFileId];
            try {
                await postCodesMutation.mutateAsync([{
                    codeset: 'corpus',
                    code: corpusId,
                    value: { ...codeEntry, files: updatedFiles }
                }]);
                queryClient.invalidateQueries('corpusCodes');
            } catch (err) {
                console.error('Error updating file metadata:', err);
            }

            refetchFiles();
            setSnackbarData({ show: true, text: intl.formatMessage({ id: 'file.deleteSuccess', defaultMessage: 'File deleted successfully' }), state: 'success', autoHide: true });
        } catch (err) {
            setSnackbarData({ show: true, text: intl.formatMessage({ id: 'file.deleteError', defaultMessage: 'Failed to delete file' }), state: 'error', autoHide: true });
        } finally {
            setIsDeletingFile(false);
            setDeleteTarget(null);
        }
    };

    if (corpusCodesQuery.isLoading) {
        return (
            <div className="survey-container knowledge-corpus-editor">
                <div className="loading-state"><Loader2 size={24} className="spinner" /></div>
            </div>
        );
    }

    if (!codeEntry) {
        return (
            <div className="survey-container knowledge-corpus-editor">
                <div className="survey-header header">
                    <Link to="/bot-competences" className="back">
                        <BackArrow />
                    </Link>
                    <h3>{intl.formatMessage({ id: 'botCompetences', defaultMessage: 'Bot Competences' })}</h3>
                </div>
                <div className="survey-view">
                    <div className="survey-content">
                        <div className="survey-edit">
                            <p style={{ color: '#999' }}>
                                {intl.formatMessage({ id: 'knowledge.notFound', defaultMessage: 'Knowledge base not found.' })}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="survey-container knowledge-corpus-editor">
            <div className="survey-header header">
                <Link to="/bot-competences" className="back">
                    <BackArrow />
                </Link>
                <h3>{title}</h3>
                <div className="change-status">
                    <button className="status-button" onClick={handleStartEditTitle}>
                        {intl.formatMessage({ id: 'edit', defaultMessage: 'Edit' })}
                    </button>
                </div>
            </div>
            <div className="survey-view">
                <div className="survey-content">
                    <div className="survey-navigation">
                        <nav>
                            <ul>
                                <li>
                                    <NavLink to={`${url}/materials`}>
                                        <Files size={16} />
                                        {intl.formatMessage({ id: 'referenceMaterials', defaultMessage: 'Materials' })}
                                    </NavLink>
                                </li>
                                <li>
                                    <NavLink to={`${url}/used-by`}>
                                        <Bot size={16} />
                                        {intl.formatMessage({ id: 'knowledge.usedBy', defaultMessage: 'Used by' })}
                                    </NavLink>
                                </li>
                            </ul>
                        </nav>
                    </div>
                    <Switch>
                        <Route path={`${path}/materials`}>
                            <div className="survey-edit">
                                <h4 className="field-header">
                                    {intl.formatMessage({ id: 'referenceMaterials', defaultMessage: 'Materials' })}
                                    {totalSize > 0 && (
                                        <div className="materials-usage-info">
                                            {formatFileSize(totalSize)} / {formatFileSize(FILE_CONFIG.MAX_TOTAL_SIZE_GEMINI)}
                                        </div>
                                    )}
                                </h4>

                                <div className="materials-list">
                                    {filesLoading ? (
                                        <div className="loading-state"><Loader2 size={20} className="spinner" /></div>
                                    ) : (
                                        <>
                                            {files.length === 0 && (
                                                <p className="placeholder">
                                                    {intl.formatMessage({ id: 'knowledge.noFiles', defaultMessage: 'No files uploaded yet. Add files to make them available to bots using this knowledge base.' })}
                                                </p>
                                            )}

                                            {files.map((f, i) => (
                                                <div key={i} className="material-item">
                                                    <File size={20} />
                                                    <div className="material-details">
                                                        <span className="material-name">{f.displayName || f.name?.split('/').pop()}</span>
                                                        <span className="material-meta">
                                                            {f.size ? formatFileSize(f.size) : ''}
                                                            {f.createTime && `${f.size ? ' • ' : ''}${intl.formatDate(new Date(f.createTime), { dateStyle: 'medium' })}`}
                                                            {f.uploadedBy && ` • ${f.uploadedBy}`}
                                                        </span>
                                                    </div>
                                                    <button
                                                        className="remove-material"
                                                        onClick={() => setDeleteTarget(f)}
                                                        title={intl.formatMessage({ id: 'survey.remove', defaultMessage: 'Remove' })}
                                                    >
                                                        <Trash size={20} />
                                                    </button>
                                                </div>
                                            ))}

                                            {Object.entries(uploadingFiles).map(([filename, fileInfo]) => {
                                                const phase = getUploadPhase(fileInfo);
                                                const elapsed = Math.floor((Date.now() - fileInfo.startTime) / 1000);
                                                return (
                                                    <div key={filename} className={`material-item uploading-item ${phase}`}>
                                                        {phase === 'success'
                                                            ? <File size={20} />
                                                            : <Loader2 size={20} className="spinner" />
                                                        }
                                                        <div className="material-details">
                                                            <span className="material-name">{filename}</span>
                                                            <span className="material-meta">
                                                                {phase === 'uploading' && intl.formatMessage({ id: 'file.uploading', defaultMessage: 'Uploading...' })}
                                                                {phase === 'indexing' && intl.formatMessage({ id: 'file.indexing', defaultMessage: 'Indexing...' })}
                                                                {phase === 'success' && intl.formatMessage({ id: 'file.uploadSuccess', defaultMessage: 'Done' })}
                                                                {phase === 'error' && intl.formatMessage({ id: 'file.uploadError', defaultMessage: 'Failed' })}
                                                                {(phase === 'uploading' || phase === 'indexing') && ` (${elapsed}s)`}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            <button
                                                className="new-topic"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={isUploading}
                                            >
                                                + {intl.formatMessage({ id: 'bot.materials.add', defaultMessage: 'Add Material' })}
                                            </button>
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                style={{ display: 'none' }}
                                                onChange={handleFileUpload}
                                                accept={FILE_CONFIG.ALLOWED_EXTENSIONS_GEMINI.join(',')}
                                                multiple
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </Route>
                        <Route path={`${path}/used-by`}>
                            <div className="survey-edit">
                                <h4 className="field-header">
                                    {intl.formatMessage({ id: 'knowledge.usedBy', defaultMessage: 'Used by' })}
                                </h4>
                                {botsQuery.isLoading ? (
                                    <div className="loading-state"><Loader2 size={20} className="spinner" /></div>
                                ) : usedByBots.length === 0 ? (
                                    <p className="placeholder">
                                        {intl.formatMessage(
                                            { id: 'knowledge.notLinkedGuide', defaultMessage: 'Not linked to any <link>bot module</link>. Link it from a module\'s Knowledge tab to let the bot use these materials when forming responses.' },
                                            { link: chunks => <Link to="/support-bots">{chunks}</Link> }
                                        )}
                                    </p>
                                ) : (
                                    <UsedByBots bots={usedByBots} />
                                )}
                            </div>
                        </Route>
                        <Route path={path}>
                            <Redirect to={`${url}/materials`} />
                        </Route>
                    </Switch>
                </div>
            </div>

            {editingTitle && (
                <Popup closePopup={() => setEditingTitle(false)}>
                    <h3>{intl.formatMessage({ id: 'knowledge.renameTitle', defaultMessage: 'Rename Knowledge Base' })}</h3>
                    <div className="create-kb-form">
                        <label>{intl.formatMessage({ id: 'knowledge.titleLabel', defaultMessage: 'Knowledge Base title' })}</label>
                        <input
                            type="text"
                            className="create-kb-input"
                            value={titleDraft}
                            onChange={e => setTitleDraft(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !isSavingTitle) handleSaveTitle(); }}
                            autoFocus
                        />
                    </div>
                    <div className="modal-options">
                        <button className="delete-kb-btn" onClick={() => {
                            setEditingTitle(false);
                            setShowDeleteKBModal(true);
                        }}>
                            <Trash size={14} />
                            {intl.formatMessage({ id: 'survey.delete', defaultMessage: 'Delete' })}
                        </button>
                        <button className="cancel" onClick={() => setEditingTitle(false)}>
                            {intl.formatMessage({ id: 'modal.cancel', defaultMessage: 'Cancel' })}
                        </button>
                        <button
                            className="confirm"
                            onClick={handleSaveTitle}
                            disabled={!titleDraft.trim() || isSavingTitle}
                        >
                            {isSavingTitle
                                ? <><Loader2 size={14} className="spinner" /> {intl.formatMessage({ id: 'saving', defaultMessage: 'Saving...' })}</>
                                : intl.formatMessage({ id: 'save', defaultMessage: 'Save' })}
                        </button>
                    </div>
                </Popup>
            )}

            {showDeleteKBModal && (
                <Modal
                    header={intl.formatMessage({ id: 'knowledge.deleteTitle', defaultMessage: 'Are you sure you want to delete this knowledge base?' })}
                    text={
                        usedByBots.length > 0
                            ? intl.formatMessage(
                                { id: 'knowledge.deleteWarning', defaultMessage: 'This knowledge base is linked to {count} bot module(s). Deleting it will remove it from those modules and delete all files related to it. This cannot be undone.' },
                                { count: usedByBots.length }
                            )
                            : intl.formatMessage({ id: 'knowledge.deleteConfirm', defaultMessage: 'Deleting it will permanently remove it and all files related to it. This cannot be undone.' })
                    }
                    closeModal={() => setShowDeleteKBModal(false)}
                    confirmAction={handleDeleteKB}
                    confirmDisabled={isDeletingKB}
                    confirmText={isDeletingKB
                        ? <><Loader2 size={14} className="spinner" /> {intl.formatMessage({ id: 'deleting', defaultMessage: 'Deleting...' })}</>
                        : intl.formatMessage({ id: 'survey.delete', defaultMessage: 'Delete' })
                    }
                    alert={true}
                />
            )}

            {deleteTarget && (
                <Modal
                    header={intl.formatMessage({ id: 'delete.material', defaultMessage: 'Delete file' })}
                    text={intl.formatMessage({ id: 'delete.material.text', defaultMessage: 'Are you sure you want to delete this file from the knowledge base?' })}
                    closeModal={() => setDeleteTarget(null)}
                    confirmAction={confirmDeleteFile}
                    confirmDisabled={isDeletingFile}
                    confirmText={isDeletingFile
                        ? <><Loader2 size={16} className="spinner" /> {intl.formatMessage({ id: 'deleting', defaultMessage: 'Deleting...' })}</>
                        : intl.formatMessage({ id: 'survey.delete', defaultMessage: 'Delete' })}
                    alert={true}
                />
            )}
        </div>
    );
}
